const EMPTY_VIDEO_PATTERNS =
  /^(tbd|n\/a|none|not available|record after)/i;

const WINDOWS_ABSOLUTE_PATH =
  /^[a-zA-Z]:[\\/]/;

const WINDOWS_UNC_PATH =
  /^\\\\/;

// Edge Mentor uses the same backend environment as the application.
// Excel stores only the video-specific filename; the server/API part is
// supplied by the application's REACT_APP_SECOND_API configuration.
const EDGE_MENTOR_API_BASE_URL = String(
  process.env.REACT_APP_SECOND_API || "",
)
  .trim()
  .replace(/\/+$/, "");

const EDGE_MENTOR_VIDEO_ROUTE =
  "/solutionsection/video/";

const EDGE_MENTOR_VIDEO_BASE_URL =
  EDGE_MENTOR_API_BASE_URL
    ? `${EDGE_MENTOR_API_BASE_URL}${EDGE_MENTOR_VIDEO_ROUTE}`
    : "";

const API_VIDEO_ROUTE_PATTERN =
  /\/solutionsection\/video\//i;

const API_UPLOAD_ROUTE_PATTERN =
  /\/solutionsection\/upload-video\/?$/i;

const STATIC_VIDEO_ROUTE_PATTERN =
  /\/uploads\/videos\//i;

const getRawVideoSource = (entry) => {
  const candidates = [
    entry?.validation_video,
    entry?.video_src,
    entry?.video_url,
    entry?.video_path,
  ];

  return (
    candidates
      .map((value) =>
        String(value ?? "").trim(),
      )
      .find(Boolean) || ""
  );
};

const normalizeHttpUrl = (source) => {
  try {
    return new URL(source).href;
  } catch {
    try {
      return encodeURI(source);
    } catch {
      return source;
    }
  }
};

const looksLikeVideoFilename = (source) =>
  /^[^/\\]+\.(mp4|mov|webm|m4v)$/i.test(
    source.trim(),
  );

const buildApiVideoUrl = (filename) => {
  const cleanFilename =
    String(filename ?? "").trim();

  if (!cleanFilename) {
    return "";
  }

  if (!EDGE_MENTOR_VIDEO_BASE_URL) {
    return "";
  }

  // encodeURIComponent converts spaces and other filename characters safely.
  return `${EDGE_MENTOR_VIDEO_BASE_URL}${encodeURIComponent(
    cleanFilename,
  )}`;
};

const classifySource = (source) => {
  if (/^https?:\/\//i.test(source)) {
    if (API_UPLOAD_ROUTE_PATTERN.test(source)) {
      return "api-upload-url";
    }

    if (API_VIDEO_ROUTE_PATTERN.test(source)) {
      return "api-video-stream-url";
    }

    if (STATIC_VIDEO_ROUTE_PATTERN.test(source)) {
      return "static-video-url";
    }

    return "http-url";
  }

  if (looksLikeVideoFilename(source)) {
    return "api-video-filename";
  }

  if (/^blob:/i.test(source)) {
    return "blob-url";
  }

  if (/^data:video\//i.test(source)) {
    return "data-video";
  }

  if (/^file:\/\//i.test(source)) {
    return "file-url";
  }

  if (source.startsWith("/")) {
    return "app-public-path";
  }

  if (
    source.startsWith("./") ||
    source.startsWith("../")
  ) {
    return "relative-path";
  }

  if (
    WINDOWS_ABSOLUTE_PATH.test(source) ||
    WINDOWS_UNC_PATH.test(source)
  ) {
    return "windows-local-path";
  }

  return "custom-or-relative-source";
};

/**
 * Resolve an Edge Mentor entry into a video source the preview can consume.
 *
 * CURRENT APIDOG WORKFLOW:
 *
 * 1. Upload manually in Apidog using the active backend environment.
 *
 * 2. API returns the uploaded filename, for example:
 *    PV Input Data Design Tab Demo.mp4
 *
 * 3. Excel stores ONLY that filename:
 *    PV Input Data Design Tab Demo.mp4
 *
 * 4. This resolver reads REACT_APP_SECOND_API and automatically creates:
 *    <REACT_APP_SECOND_API>/solutionsection/video/
 *    PV%20Input%20Data%20Design%20Tab%20Demo.mp4
 *
 * 5. EdgeMentorMessage.jsx receives the resulting playable URL.
 *
 * Full HTTP/HTTPS URLs remain supported for backward compatibility.
 */
export function resolveEdgeMentorVideoSource(
  entry,
) {
  const rawSource = getRawVideoSource(entry);

  if (
    !rawSource ||
    EMPTY_VIDEO_PATTERNS.test(rawSource)
  ) {
    return {
      configured: false,
      playable: false,
      rawSource: "",
      src: "",
      sourceType: "none",
      reason: "missing",
    };
  }

  const sourceType =
    classifySource(rawSource);

  if (sourceType === "api-upload-url") {
    return {
      configured: true,
      playable: false,
      rawSource,
      src: "",
      sourceType,
      reason: "upload-url-is-not-playable",
    };
  }

  if (sourceType === "windows-local-path") {
    return {
      configured: true,
      playable: false,
      rawSource,
      src: "",
      sourceType,
      reason: "unsupported-local-path",
    };
  }

  if (sourceType === "api-video-filename") {
    const apiVideoUrl =
      buildApiVideoUrl(rawSource);

    if (!apiVideoUrl) {
      return {
        configured: true,
        playable: false,
        rawSource,
        src: "",
        sourceType,
        reason: "missing-api-base-url",
      };
    }

    return {
      configured: true,
      playable: true,
      rawSource,
      src: apiVideoUrl,
      sourceType,
      reason: "",
    };
  }

  const src =
    /^https?:\/\//i.test(rawSource)
      ? normalizeHttpUrl(rawSource)
      : rawSource;

  return {
    configured: true,
    playable: true,
    rawSource,
    src,
    sourceType,
    reason: "",
  };
}

export {
  EDGE_MENTOR_API_BASE_URL,
  EDGE_MENTOR_VIDEO_ROUTE,
  EDGE_MENTOR_VIDEO_BASE_URL,
  buildApiVideoUrl,
};

export default resolveEdgeMentorVideoSource;
